import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { syncFromCloud, disableCloudSync } from "../utils/storage";

const S = {
  modal: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.6)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
    padding: "20px",
  },
  panel: {
    background: "white",
    width: "100%",
    maxWidth: "480px",
    borderRadius: "16px",
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
    overflow: "hidden",
    direction: "rtl",
  },
  header: {
    background: "linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)",
    padding: "16px 20px",
    color: "white",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  closeBtn: {
    background: "rgba(255, 255, 255, 0.2)",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    color: "white",
    width: "32px",
    height: "32px",
    borderRadius: "8px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
  },
  body: {
    padding: "24px",
  },
  card: (selected) => ({
    border: selected ? "2px solid #2563eb" : "2px solid #e2e8f0",
    borderRadius: "12px",
    padding: "16px",
    marginBottom: "12px",
    cursor: "pointer",
    background: selected ? "#eff6ff" : "white",
    transition: "all 0.2s",
  }),
  cardTitle: {
    fontSize: "16px",
    fontWeight: 600,
    marginBottom: "4px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  cardDesc: {
    fontSize: "13px",
    color: "#64748b",
    margin: 0,
  },
  syncInfo: {
    marginTop: "16px",
    padding: "12px",
    background: "#f8fafc",
    borderRadius: "8px",
    fontSize: "13px",
    color: "#475569",
  },
  syncBtn: {
    marginTop: "8px",
    padding: "6px 14px",
    background: "#2563eb",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "13px",
  },
  confirmOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2100,
  },
  confirmBox: {
    background: "white",
    borderRadius: "12px",
    padding: "24px",
    maxWidth: "400px",
    width: "90%",
    direction: "rtl",
    textAlign: "right",
  },
  confirmBtns: {
    display: "flex",
    gap: "8px",
    marginTop: "16px",
    justifyContent: "flex-start",
  },
  btnPrimary: {
    padding: "8px 16px",
    background: "#2563eb",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
  },
  btnSecondary: {
    padding: "8px 16px",
    background: "#e2e8f0",
    color: "#334155",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
  },
  btnDanger: {
    padding: "8px 16px",
    background: "#ef4444",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
  },
};

export default function StorageSettingsModal({ onClose }) {
  const { storageMode, setStorageMode, syncStatus, lastSyncTime, syncError } = useAuth();
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const handleSelect = (mode) => {
    if (mode === storageMode) return;
    if (mode === 'cloud') {
      setConfirm('toCloud');
    } else {
      setConfirm('toLocal');
    }
  };

  const doSwitch = async (mode, deleteRemote = false) => {
    setBusy(true);
    try {
      if (mode === 'local' && deleteRemote) {
        await disableCloudSync(true);
      }
      await setStorageMode(mode);
    } catch (err) {
      console.error('[StorageSettings] switch failed:', err);
    }
    setBusy(false);
    setConfirm(null);
  };

  const doManualSync = async () => {
    setBusy(true);
    try {
      await syncFromCloud();
    } catch (err) {
      console.error('[StorageSettings] manual sync failed:', err);
    }
    setBusy(false);
  };

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={S.panel} onClick={e => e.stopPropagation()}>
        <div style={S.header}>
          <span style={{ fontSize: "16px", fontWeight: 600 }}>הגדרות אחסון</span>
          <button style={S.closeBtn} onClick={onClose}>&times;</button>
        </div>
        <div style={S.body}>
          <div
            style={S.card(storageMode === 'cloud')}
            onClick={() => handleSelect('cloud')}
          >
            <div style={S.cardTitle}>
              <span>&#9729;</span> ענן (סנכרון)
            </div>
            <p style={S.cardDesc}>
              הנתונים מסונכרנים לענן. גישה ממכשירים מרובים.
            </p>
          </div>

          <div
            style={S.card(storageMode === 'local')}
            onClick={() => handleSelect('local')}
          >
            <div style={S.cardTitle}>
              <span>&#128187;</span> מקומי בלבד
            </div>
            <p style={S.cardDesc}>
              הנתונים נשמרים במכשיר זה בלבד. פרטיות מלאה.
            </p>
          </div>

          {storageMode === 'cloud' && (
            <div style={S.syncInfo}>
              <div>
                סטטוס:{' '}
                {syncStatus === 'syncing' ? 'מסנכרן...' :
                 syncStatus === 'error' ? 'שגיאה בסנכרון' :
                 'מסונכרן'}
              </div>
              {syncStatus === 'error' && syncError && (
                <div style={{ marginTop: 6, padding: 8, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 12 }}>
                  {syncError}
                </div>
              )}
              {lastSyncTime && (
                <div style={{ marginTop: "4px" }}>
                  סנכרון אחרון: {lastSyncTime.toLocaleTimeString('he-IL')}
                </div>
              )}
              <button
                style={{ ...S.syncBtn, opacity: busy ? 0.6 : 1 }}
                onClick={doManualSync}
                disabled={busy}
              >
                {busy ? 'מסנכרן...' : 'סנכרן עכשיו'}
              </button>
            </div>
          )}
        </div>
      </div>

      {confirm === 'toCloud' && (
        <div style={S.confirmOverlay} onClick={() => setConfirm(null)}>
          <div style={S.confirmBox} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 600, marginBottom: "8px" }}>הפעלת סנכרון ענן</div>
            <p style={{ fontSize: "14px", color: "#475569" }}>
              פעולה זו תעלה את כל הנתונים שלך לשרתי הענן. הנתונים יהיו נגישים ממכשירים מרובים.
            </p>
            <div style={S.confirmBtns}>
              <button
                style={{ ...S.btnPrimary, opacity: busy ? 0.6 : 1 }}
                onClick={() => doSwitch('cloud')}
                disabled={busy}
              >
                {busy ? 'מעלה...' : 'אשר והפעל'}
              </button>
              <button style={S.btnSecondary} onClick={() => setConfirm(null)}>ביטול</button>
            </div>
          </div>
        </div>
      )}

      {confirm === 'toLocal' && (
        <div style={S.confirmOverlay} onClick={() => setConfirm(null)}>
          <div style={S.confirmBox} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 600, marginBottom: "8px" }}>מעבר לאחסון מקומי</div>
            <p style={{ fontSize: "14px", color: "#475569" }}>
              הנתונים ימשיכו להישמר במכשיר זה בלבד. האם למחוק את הנתונים מהענן?
            </p>
            <div style={S.confirmBtns}>
              <button
                style={{ ...S.btnPrimary, opacity: busy ? 0.6 : 1 }}
                onClick={() => doSwitch('local', false)}
                disabled={busy}
              >
                {busy ? 'מעביר...' : 'השאר בענן'}
              </button>
              <button
                style={{ ...S.btnDanger, opacity: busy ? 0.6 : 1 }}
                onClick={() => doSwitch('local', true)}
                disabled={busy}
              >
                {busy ? 'מוחק...' : 'מחק מהענן'}
              </button>
              <button style={S.btnSecondary} onClick={() => setConfirm(null)}>ביטול</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
